import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import { HierarchyNode } from '../types/hierarchy';
import '../App.css';

interface Location {
  id: number;
  name: string;
  code?: string;
  organization_id: number;
  organizationName?: string;
}

interface LocationHierarchy {
  id: number;
  location_id: number;
  hierarchy_node_id: number;
  locationName?: string;
  hierarchyNodeName?: string;
}

const LocationHierarchyPage: React.FC = () => {
  const { user } = useAuthStore();
  const [locations, setLocations] = useState<Location[]>([]);
  const [hierarchyNodes, setHierarchyNodes] = useState<HierarchyNode[]>([]);
  const [associations, setAssociations] = useState<LocationHierarchy[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<number | ''>('');
  const [selectedNode, setSelectedNode] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchLocations(),
        fetchHierarchyNodes(),
        fetchAssociations()
      ]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/locations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    if (data.success) {
      setLocations(data.data);
    }
  };

  const fetchHierarchyNodes = async () => {
    // Get all hierarchy nodes across all levels
    const levelsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const levelsData = await levelsResponse.json();
    
    if (levelsData.success) {
      const allNodes: HierarchyNode[] = [];
      
      for (const level of levelsData.data) {
        const nodesResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels/${level.id}/nodes`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const nodesData = await nodesResponse.json();
        
        if (nodesData.success) {
          const nodesWithLevel = nodesData.data.map((node: HierarchyNode) => ({
            ...node,
            levelName: level.name
          }));
          allNodes.push(...nodesWithLevel);
        }
      }
      
      setHierarchyNodes(allNodes);
    }
  };

  const fetchAssociations = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/location-hierarchy`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    if (data.success) {
      setAssociations(data.data);
    }
  };

  const handleCreateAssociation = async () => {
    if (!selectedLocation || !selectedNode) {
      setError('Please select both a location and hierarchy node');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/location-hierarchy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          location_id: selectedLocation,
          hierarchy_node_id: selectedNode
        })
      });

      const data = await response.json();
      if (data.success) {
        setSelectedLocation('');
        setSelectedNode('');
        fetchAssociations(); // Refresh the list
        setError(null);
      } else {
        setError(data.error || 'Failed to create association');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteAssociation = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this association?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/location-hierarchy/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchAssociations(); // Refresh the list
      } else {
        setError(data.error || 'Failed to delete association');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getLocationName = (locationId: number) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  };

  const getNodeName = (nodeId: number) => {
    const node = hierarchyNodes.find(n => n.id === nodeId);
    if (!node) return `Node ${nodeId}`;
    
    const parentInfo = node.parentNodeName ? ` → Parent: ${node.parentNodeName}` : '';
    return `${node.name} (${node.levelName})${parentInfo}`;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Location-Hierarchy Associations</h1>
            <p>Associate locations with hierarchy nodes to define organizational structure</p>
          </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

          {/* Create New Association */}
          <div className="card">
        <h2>Create New Association</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">Select a location...</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name} ({location.organizationName})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Hierarchy Node</label>
            <select
              value={selectedNode}
              onChange={(e) => setSelectedNode(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">Select a hierarchy node...</option>
              {hierarchyNodes.map(node => (
                <option key={node.id} value={node.id}>
                  {node.name} ({node.levelName}){node.parentNodeName ? ` → Parent: ${node.parentNodeName}` : ''} - {node.organizationName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button 
            className="primary-button"
            onClick={handleCreateAssociation}
            disabled={!selectedLocation || !selectedNode}
          >
            Create Association
          </button>
        </div>
      </div>

          {/* Existing Associations */}
          <div className="card">
        <h2>Current Associations</h2>
        {associations.length === 0 ? (
          <p>No location-hierarchy associations found.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Hierarchy Node</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {associations.map(association => (
                  <tr key={association.id}>
                    <td>{getLocationName(association.location_id)}</td>
                    <td>{getNodeName(association.hierarchy_node_id)}</td>
                    <td>
                      <button
                        className="danger-button"
                        onClick={() => handleDeleteAssociation(association.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationHierarchyPage;